/* global Config */

import {difference, every, filter, get, includes, isEqual, map, union} from 'lodash';
import {REHYDRATE} from 'redux-persist/es/constants';
import socket from '../lib/app/socket';

// Lib Core
import logger from '../lib/core/logger';

const recordLockingMiddleware = (store) => (next) => (action) => {
    const {dispatch, getState} = store;
    const {type} = action || {};

    const state = getState();
    next(action);
    const nextState = getState();

    const actions = {
        AUTHENTICATE_FETCH_FULFILLED: () => startListening(dispatch, nextState),
        [REHYDRATE]: () => startListening(dispatch, nextState),
        SOCKET_RECONNECT: () => reconnectListening(dispatch, nextState),
        AUTHENTICATE_LOGOUT: () => stopListening(),
        RECORD_LOCKING_SET_LOCKED_OVERRIDE: () => lockOverride(dispatch, action.records),
        RECORD_LOCKING_SET_LOCKED: () => setLockedRecords(dispatch, getLockingStates(state, nextState)),
        RECORD_LOCKING_LOCK_RECORD: () => handleLock(dispatch, action),
        RECORD_LOCKING_UNLOCK_RECORD: () => handleUnlock(dispatch, nextState),
        RECORD_LOCKING_LOGIN: () => setLockedRecords(dispatch, getLockingStates(state, nextState)),
        RECORD_LOCKING_UPDATE: () => setLockedRecords(dispatch, getLockingStates(state, nextState)),
        RECORD_LOCKING_CHECK_LOCKS: () => checkLocks(dispatch),
    };

    if (actions[type]) {
        actions[type]();
    }
};

function handleLock(dispatch, {component, serviceKey, recordId}) {
    dispatch({
        component,
        recordIds: [recordId],
        serviceKey,
        type: 'RECORD_LOCKING_SET_LOCKED',
    });
}

function handleUnlock(dispatch, {recordLocking: {recordLockCount = {}} = {}}) {
    map(recordLockCount, (componentLocks, component) => {
        map(componentLocks, (serviceLocks, serviceKey) => {
            if (every(serviceLocks, (lockCount) => lockCount <= 0)) {
                dispatch({
                    component,
                    recordIds: [],
                    serviceKey,
                    type: 'RECORD_LOCKING_SET_LOCKED',
                });
            }
        });
    });
}

function checkLocks(dispatch) {
    socketSend(dispatch, 'LOCK_CHECK');
}

function lockOverride(dispatch, records) {
    socketSend(dispatch, 'LOCK_OVERRIDE', records);
}

/** Diffs previous vs next lock sets per service, filtering by record count to avoid overriding other users' active locks. */
function setLockedRecords(dispatch, {loggedIn, nextLockedRecords, nextRecordsToLock, recordsToLock, username}) {
    if (loggedIn && username) {
        map(nextRecordsToLock, (recordIds = [], serviceKey) => {
            const prevRecordIds = recordsToLock[serviceKey];
            const newRecordsToLock = !isEqual(prevRecordIds, recordIds);
            const lockedRecordIds = map(get(nextLockedRecords, serviceKey, []), 'recordId');

            if (newRecordsToLock) {
                let recordIdsToLock = [];

                // if there's multiple records to be locked, be sure to include the existing locked record
                if (recordIds.length > 1) {
                    recordIdsToLock = filter(
                        recordIds,
                        (recordId) =>
                            filter(
                                get(nextLockedRecords, serviceKey, []),
                                (record) => recordId !== record.recordId || record.username === username
                            ).length
                    );
                } else {
                    recordIdsToLock = difference(recordIds, lockedRecordIds);
                }

                if (difference(recordIds, recordIdsToLock).length && recordIdsToLock.length === 1) {
                    socketSend(dispatch, 'SET_LOCKED_RECORDS', {recordIds: [], serviceKey});
                } else {
                    socketSend(dispatch, 'SET_LOCKED_RECORDS', {recordIds: recordIdsToLock, serviceKey});
                }
            } else if (prevRecordIds.length === 1 && !includes(lockedRecordIds, prevRecordIds[0])) {
                // If a record becomes available, lock it
                socketSend(dispatch, 'SET_LOCKED_RECORDS', {recordIds: prevRecordIds, serviceKey});
            }
        });
    }
}

function getLockingStates(state = {}, nextState = {}) {
  const nextRL = nextState.recordLocking || {};
  const prevRL = state.recordLocking || {};

  return {
    loggedIn: nextRL.loggedIn,
    nextLockedRecords: nextRL.lockedRecords,
    nextRecordsToLock: getCombinedRecordsToLock(nextRL.recordsToLock),
    recordsToLock: getCombinedRecordsToLock(prevRL.recordsToLock),
    username: get(nextState, 'auth.user'),
  };
}

function getCombinedRecordsToLock(recordsToLock) {
    const currentRecordsToLock = {};

    map(recordsToLock, (component, serviceKey) => {
        map(component, (recordIds) => {
            currentRecordsToLock[serviceKey] = union(currentRecordsToLock[serviceKey] || [], recordIds);
        });
    });

    return currentRecordsToLock;
}

function socketSend(dispatch, event, records = {}) {
    socket.send(
        {
            event,
            records,
        },
        (response) => socketResponse(dispatch, response, event)
    );
}

function socketResponse(dispatch, {error, lockedRecords, response, success}) {
    if (error && error.length) {
        dispatch({type: 'RECORD_LOCKING_ERROR', error});
        dispatch({autoClose: false, type: 'GLOBAL_RESPONSE_ADD', error, success: response});
        logger.error('RECORD_LOCKING_ERROR', error);
    } else {
        logger.info('SOCKET_RESPONSE', response, lockedRecords, {success});
    }
}

function loginResponse(dispatch, response) {
    dispatch({type: 'RECORD_LOCKING_LOGIN', loggedIn: get(response, 'success')});
    socketResponse(dispatch, response);
}

function startListening(dispatch, nextState) {
    const authState = nextState.auth || {};
    const {accessToken, user} = authState;

    if (Config.socket?.enabled && !socket.connected && accessToken && user) {
        socket.off();

        socket.on('disconnect', () => {
            dispatch({type: 'RECORD_LOCKING_RESET'});
        });

        socket.on('message', ({event, lockedRecords}) => {
            if (event === 'LOCKS_CHANGED') {
                dispatch({type: 'RECORD_LOCKING_UPDATE', lockedRecords, user});
            }
        });

        socket.on('error', (error) => {
            logger.error('SOCKET ERROR: ', error);
        });

        socket.on('reconnect', () => {
            dispatch({type: 'SOCKET_RECONNECT'});
        });

        socket.send(
            {
                event: 'LOGIN',
                userId: accessToken,
                username: user,
            },
            (response) => loginResponse(dispatch, response)
        );

        socket.open();
    }
}

function reconnectListening(dispatch, nextState) {
    startListening(dispatch, nextState);
}

function stopListening() {
    socket.off();
    socket.close();
}

export default recordLockingMiddleware;
